import { z } from "zod";

import { schemes } from "../schema-validation";
import { baseEventInput } from "./baseEvent";

export const ECompanyOnboardingTaskStatus = {
  INCOMPLETE: "incomplete",
  IN_REVIEW: "in-review",
  COMPLETE: "complete",
  REJECTED: "rejected",
} as const;

export type TCompanyOnboardingTaskStatus =
  (typeof ECompanyOnboardingTaskStatus)[keyof typeof ECompanyOnboardingTaskStatus];

export const companyOnboardingTaskStatusEnum = z.enum([
  ECompanyOnboardingTaskStatus.INCOMPLETE,
  ECompanyOnboardingTaskStatus.IN_REVIEW,
  ECompanyOnboardingTaskStatus.COMPLETE,
  ECompanyOnboardingTaskStatus.REJECTED,
]);

export const companyOnboardingTaskStatusSchema = z.preprocess((val) => {
  if (val === true || val === "true") {
    return ECompanyOnboardingTaskStatus.COMPLETE;
  }
  if (val === false || val === "false") {
    return ECompanyOnboardingTaskStatus.INCOMPLETE;
  }
  return val;
}, companyOnboardingTaskStatusEnum);

const normalizeCompanyOnboardingTaskStatusValue = (
  value: unknown
): TCompanyOnboardingTaskStatus => {
  const parsed = companyOnboardingTaskStatusSchema.safeParse(value);
  return parsed.success ? parsed.data : ECompanyOnboardingTaskStatus.INCOMPLETE;
};

export const mergeCompanyOnboardingTaskFieldValues = (
  existing: unknown,
  incoming: unknown
): TCompanyOnboardingTaskStatus => {
  const a = normalizeCompanyOnboardingTaskStatusValue(existing);
  const b = normalizeCompanyOnboardingTaskStatusValue(incoming);
  if (
    a === ECompanyOnboardingTaskStatus.COMPLETE ||
    b === ECompanyOnboardingTaskStatus.COMPLETE
  ) {
    return ECompanyOnboardingTaskStatus.COMPLETE;
  }
  if (
    a === ECompanyOnboardingTaskStatus.IN_REVIEW ||
    b === ECompanyOnboardingTaskStatus.IN_REVIEW
  ) {
    return ECompanyOnboardingTaskStatus.IN_REVIEW;
  }
  if (
    a === ECompanyOnboardingTaskStatus.REJECTED ||
    b === ECompanyOnboardingTaskStatus.REJECTED
  ) {
    return ECompanyOnboardingTaskStatus.REJECTED;
  }
  return ECompanyOnboardingTaskStatus.INCOMPLETE;
};

export const companyOnboardingEvent = baseEventInput.merge(
  z.object({
    source: z.literal("company.onboarding"),
    companyId: schemes.uuid(),
    taskDefinitionId: schemes.uuid(),
    payload: z
      .object({
        businessDetails: companyOnboardingTaskStatusSchema,
        ownerDetails: companyOnboardingTaskStatusSchema,
        agreements: companyOnboardingTaskStatusSchema,
        bankingDetails: companyOnboardingTaskStatusSchema,
        businessDocuments: companyOnboardingTaskStatusSchema,
        existingPayrollMigration: companyOnboardingTaskStatusSchema,
        workLocation: companyOnboardingTaskStatusSchema,
        worker: companyOnboardingTaskStatusSchema,
        federalTaxCard: companyOnboardingTaskStatusSchema,
      })
      .partial(),
  })
);

export type CompanyOnboardingEvent = z.infer<typeof companyOnboardingEvent>;
